import React, { useRef, useCallback, useEffect } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";

const EmailEditor = ({
  editorHtml,
  loadingTemplate,
  height = 50, // in theme spacing units
  onContentChange,
  title = "Email Editor",
}) => {
  const theme = useTheme();
  const onContentChangeRef = useRef(onContentChange);
  const iframeRef = useRef(null);
  const lastEditorHtmlRef = useRef(editorHtml);

  // Update the ref when the callback changes but don't cause re-render
  onContentChangeRef.current = onContentChange;

  // Update iframe content when editorHtml prop changes
  useEffect(() => {
    if (
      editorHtml &&
      editorHtml !== lastEditorHtmlRef.current &&
      iframeRef.current
    ) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;

      if (doc && iframe.dataset.initialized === "true") {
        try {
          // Update the content directly in the existing iframe
          doc.open();
          doc.write(editorHtml);
          doc.close();

          // Re-apply editor styles and settings after content update
          const reapplyEditorSettings = () => {
            if (!doc.body) {
              setTimeout(reapplyEditorSettings, 10);
              return;
            }

            doc.designMode = "on";
            doc.body.contentEditable = true;
            doc.body.style.outline = "none";
            doc.body.style.padding = "16px";
            doc.body.style.margin = "0";
            doc.body.style.minHeight = "400px";
            doc.body.style.fontSize = "14px";
            doc.body.style.fontFamily = "Arial, sans-serif";
            doc.body.style.lineHeight = "1.5";

            // Update stored content
            iframe.dataset.currentContent = doc.body.innerHTML;
          };

          reapplyEditorSettings();
        } catch (error) {
          console.error("EmailEditor: Failed to update iframe content:", error);
        }
      }

      lastEditorHtmlRef.current = editorHtml;
    }
  }, [editorHtml]);

  // Expose method to get current content without triggering callbacks
  const getCurrentContent = useCallback(() => {
    if (iframeRef.current?.dataset.currentContent) {
      return iframeRef.current.dataset.currentContent;
    }
    if (iframeRef.current?.contentDocument?.body) {
      return iframeRef.current.contentDocument.body.innerHTML;
    }
    return "";
  }, []);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        overflow: "hidden",
        height: theme.spacing(height),
        position: "relative",
      }}
    >
      {loadingTemplate && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <iframe
        ref={(iframe) => {
          iframeRef.current = iframe;
          if (iframe && editorHtml && !iframe.dataset.initialized) {
            // Only initialize once when iframe first loads
            const initializeIframe = () => {
              const doc =
                iframe.contentDocument || iframe.contentWindow.document;

              if (!doc) {
                setTimeout(initializeIframe, 50);
                return;
              }

              try {
                // Write initial content
                doc.open();
                doc.write(editorHtml);
                doc.close();

                // Wait for content to be ready
                const setupEditor = () => {
                  if (!doc.body) {
                    setTimeout(setupEditor, 10);
                    return;
                  }

                  // Make document editable
                  doc.designMode = "on";
                  doc.body.contentEditable = true;
                  doc.body.style.outline = "none";
                  doc.body.style.padding = "16px";
                  doc.body.style.margin = "0";
                  doc.body.style.minHeight = "400px";
                  doc.body.style.fontSize = "14px";
                  doc.body.style.fontFamily = "Arial, sans-serif";
                  doc.body.style.lineHeight = "1.5";

                  // Store content in iframe data attribute instead of calling parent immediately
                  let contentChangeTimeout;
                  const notifyContentChange = () => {
                    // Store current content in iframe for retrieval
                    if (doc.body) {
                      iframe.dataset.currentContent = doc.body.innerHTML;
                    }

                    // Only notify parent after significant delay to avoid re-render issues
                    if (contentChangeTimeout) {
                      clearTimeout(contentChangeTimeout);
                    }
                    contentChangeTimeout = setTimeout(() => {
                      if (
                        onContentChangeRef.current &&
                        iframe.dataset.currentContent
                      ) {
                        onContentChangeRef.current(
                          iframe.dataset.currentContent
                        );
                      }
                    }, 300); // Wait 300ms after user stops typing
                  };

                  // Add event listeners with passive options where possible
                  doc.addEventListener("input", notifyContentChange, {
                    passive: true,
                  });
                  doc.addEventListener("paste", notifyContentChange);
                  doc.addEventListener("cut", notifyContentChange);

                  // Focus handling
                  const ensureFocus = () => {
                    if (doc.activeElement !== doc.body) {
                      doc.body.focus();
                    }
                  };

                  doc.addEventListener("click", ensureFocus);

                  // Mark as initialized to prevent re-initialization
                  iframe.dataset.initialized = "true";
                  // Store the initial content reference
                  lastEditorHtmlRef.current = editorHtml;
                };

                setupEditor();
              } catch (error) {
                console.log(error);
              }
            };

            initializeIframe();
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          backgroundColor: "white",
        }}
        title={title}
        sandbox="allow-same-origin allow-scripts"
      />
    </Box>
  );
};

export default EmailEditor;
